// TimeNotSyncWithServerModal.jsx
import React, { useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import ConfirmModal from '../component/ConfirmModal.jsx';

import ModalView from '../container/ModalView.js';

import { TranslationNamespace } from '../resource/translationNamespace.js';
import { ButtonId, ModalId } from '../resource/trackingConstants.js';
import { NULL_FUNCTION } from '../resource/defaults.js';

import FadeIn from '../style/FadeIn.js';
import FadeOut from '../style/FadeOut.js';
import { LIVESTREAM_TIME_DRIFT_THRESHOLD_SECONDS } from '../RemoteConfigKeys.js';
import env from '../resource/env.js';
import { color } from '../style/variables.js';

const TRACKING_MODAL_ID = ModalId.LiveStream.ModalLivestreamTimeNotSync;
let hasShownTimeNotSyncWithServerModal = false;

export const getSignedTimeOffsetSecondsWithServer = seconds =>
  seconds > 0 ? `+${seconds}` : `${seconds}`;

export const isTimeOffsetExceeded = ({
  clientTimestamp = 0,
  serverTimestamp = 0,
  thresholdSeconds,
}) => Math.abs(serverTimestamp - clientTimestamp) > thresholdSeconds * 1000;

export const TimeNotSyncWithServerModal = ({
  streamId = null,
  clientTimestamp = 0,
  serverTimestamp = 0,
  timeOffsetSecondsWithServer = 0,
  timeDriftThresholdSeconds = env[LIVESTREAM_TIME_DRIFT_THRESHOLD_SECONDS],
  toggleModal = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();
  const closeModal = useCallback(
    () =>
      toggleModal({
        modalId: 'TimeNotSyncWithServerModal',
        isOpen: false,
      }),
    [toggleModal]
  );
  const trackingPayload = useMemo(
    () => ({
      modalId: TRACKING_MODAL_ID,
      extraProps: {
        'stream.id': streamId,
        'time.server': serverTimestamp,
        'time.client': clientTimestamp,
        'time.offsetSeconds': timeOffsetSecondsWithServer,
      },
    }),
    [clientTimestamp, serverTimestamp, streamId, timeOffsetSecondsWithServer]
  );
  const signedTimeOffsetSecondsWithServer = useMemo(
    () => getSignedTimeOffsetSecondsWithServer(timeOffsetSecondsWithServer),
    [timeOffsetSecondsWithServer]
  );
  useEffect(() => {
    if (
      !hasShownTimeNotSyncWithServerModal &&
      isTimeOffsetExceeded({
        clientTimestamp,
        serverTimestamp,
        thresholdSeconds: timeDriftThresholdSeconds,
      })
    ) {
      toggleModal({
        modalId: 'TimeNotSyncWithServerModal',
        isOpen: true,
      });
      hasShownTimeNotSyncWithServerModal = true;
    }
  }, [
    clientTimestamp,
    serverTimestamp,
    timeDriftThresholdSeconds,
    toggleModal,
  ]);
  return (
    <ModalView id="TimeNotSyncWithServerModal" modalId={TRACKING_MODAL_ID}>
      <StyledTimeNotSyncWithServerModal
        data-element_id={ButtonId.All.ButtonClose}
        data-tracking_payload={trackingPayload}
        onClick={closeModal}
      >
        <ConfirmModal
          body={t('livestream_popup_time_dont_match', {
            ns: TranslationNamespace.LIVESTREAM,
            seconds: signedTimeOffsetSecondsWithServer,
          })}
          confirmI18nKey="i_got_it"
          confirmI18nNameSpace={TranslationNamespace.GENERAL}
          modalId={TRACKING_MODAL_ID}
          shouldShowCancel={false}
          onConfirm={closeModal}
        />
      </StyledTimeNotSyncWithServerModal>
    </ModalView>
  );
};

TimeNotSyncWithServerModal.propTypes = {
  streamId: PropTypes.string,
  clientTimestamp: PropTypes.number,
  serverTimestamp: PropTypes.number,
  timeOffsetSecondsWithServer: PropTypes.number,
  timeDriftThresholdSeconds: PropTypes.number,
  toggleModal: PropTypes.func,
};

const StyledTimeNotSyncWithServerModal = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${color.neutral['0_alpha_70']};
  animation: 0.6s forwards
    ${({ status }) => ('closing' === status ? FadeOut : FadeIn)};
`;

export default TimeNotSyncWithServerModal;
